import React, { useMemo, useEffect, useCallback } from 'react';
import { Box, Text, Menu, MenuButton, MenuList, Checkbox, VStack, Divider } from '@chakra-ui/core';
import { FaChevronDown } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import colors from '../../styles/colors';
import { StatusOptions } from './types';
import { useStoreActions, useStoreState } from '../../models/hooks';

type StatusFilterProps = {
  onCompletedFilterChange: (needCompleted: boolean) => void;
  // setGroupedByStatusItems: (status: string[]) => void;
  // setGroupByStatus: React.Dispatch<React.SetStateAction<string[]>>;
};

const RecruiterToDoStatusFilterComponent = ({
  onCompletedFilterChange,
}: // setGroupedByStatusItems,
// setGroupByStatus,
StatusFilterProps): JSX.Element => {
  const { t } = useTranslation();
  const { statusFilters } = useStoreState((state) => state.todo);
  const { setStatusFilters } = useStoreActions((actions) => actions.todo);

  // const [selectedOptions, setSelectedOptions] = useState<string[]>([StatusOptions.TODO, StatusOptions.IN_PROGRESS_ME]);
  const allOptions = React.useMemo(
    () => [
      {
        status: 'TODO',
        value: StatusOptions.TODO,
        label: t('toDoList:toDo'),
      },
      {
        status: 'IN_PROGRESS',
        value: StatusOptions.IN_PROGRESS_ME,
        label: t('toDoList:inProgressForMe'),
      },
      {
        status: 'IN_PROGRESS',
        value: StatusOptions.IN_PROGRESS_OTHERS,
        label: t('toDoList:inProgressForOthers'),
      },
      {
        status: 'COMPLETED',
        value: StatusOptions.COMPLETED,
        label: t('toDoList:completed'),
      },
    ],
    [t],
  );

  const selectAllOption = useMemo(
    () => ({
      value: 'Select All',
      label: t('toDoList:selectAll'),
    }),
    [t],
  );

  const isAllSelected = statusFilters.length === allOptions.length;

  const handleSelectAll = useCallback(() => {
    if (isAllSelected) {
      setStatusFilters([StatusOptions.TODO]);
    } else {
      setStatusFilters(allOptions.map((opt) => opt.value));
    }
  }, [isAllSelected, setStatusFilters, allOptions]);

  const handleCheckboxChange = (value: StatusOptions) => {
    const newOptions = statusFilters.includes(value) ? statusFilters.filter((item) => item !== value) : [...statusFilters, value];
    if (newOptions.length === allOptions.length) {
      allOptions.map((o) => o.value);
    }

    setStatusFilters(newOptions);
  };

  useEffect(() => {
    onCompletedFilterChange(statusFilters.includes(StatusOptions.COMPLETED));
    // setGroupedByStatusItems(statusFilters);
    // setGroupByStatus(statusFilters);
  }, [onCompletedFilterChange, statusFilters]);

  return (
    <Menu>
      <MenuButton as={Box} cursor="pointer" data-testid="status-filter-button">
        <Box display="inline-flex" alignItems="center">
          <Text textTransform="none" whiteSpace="nowrap" data-testid="status-text">
            {t('toDoList:status')}
          </Text>
          <Box
            width="20px"
            height="20px"
            ml={2}
            backgroundColor="#D4D8F1"
            borderRadius="6px"
            display="flex"
            justifyContent="center"
            alignItems="center"
            data-testid="chevron-icon"
          >
            <FaChevronDown size={14} color={colors.persianBlue} />
          </Box>
        </Box>
      </MenuButton>
      <MenuList p={4} data-testid="status-filter-menu">
        <VStack align="start" spacing={4}>
          <Checkbox
            isChecked={isAllSelected}
            onChange={handleSelectAll}
            fontSize="14px"
            fontWeight={400}
            data-testid="select-all-checkbox"
          >
            {selectAllOption.label}
          </Checkbox>
          <Divider />
          {allOptions.map((option) => (
            <Checkbox
              key={option.value}
              isChecked={statusFilters.includes(option.value)}
              fontSize="14px"
              fontWeight={400}
              onChange={() => handleCheckboxChange(option.value)}
              isDisabled={option.value === 'To Do'}
              colorScheme={option.value === 'To Do' ? 'gray' : 'blue'}
              data-testid={`checkbox-${option.value}`}
            >
              {option.label}
            </Checkbox>
          ))}
        </VStack>
      </MenuList>
    </Menu>
  );
};

export const RecruiterToDoStatusFilter = React.memo(RecruiterToDoStatusFilterComponent);
